/**
 * @fileoverview Video upload utilities.
 *
 * Provides client-side validation, metadata extraction, and thumbnail generation
 * for video uploads before sending to the backend.
 */

import { SUPPORTED_VIDEO_FORMATS, SUPPORTED_VIDEO_EXTENSIONS, SUPPORTED_VIDEO_LABELS } from '@/types/shared'

/**
 * Allowed video MIME types (derived from shared config).
 */
const ALLOWED_MIME_TYPES = Object.keys(SUPPORTED_VIDEO_FORMATS)

/**
 * Allowed file extensions (derived from shared config).
 */
const ALLOWED_EXTENSIONS: readonly string[] = SUPPORTED_VIDEO_EXTENSIONS

/**
 * Map from extension to MIME type for formats where browsers don't report correctly.
 * Derived from shared config.
 */
const EXTENSION_TO_MIME: Record<string, string> = Object.fromEntries(
  Object.entries(SUPPORTED_VIDEO_FORMATS).map(([mime, { extension }]) => [extension, mime]),
)

/**
 * Extensions that browsers cannot play natively.
 * For these formats, we skip client-side metadata extraction and let the backend handle it.
 */
const BROWSER_UNSUPPORTED_EXTENSIONS = ['.ts', '.avi', '.mkv']

/**
 * Checks if a file format can be played by the browser.
 * Used to determine whether to attempt client-side metadata extraction.
 *
 * @param file - The video file to check
 * @returns True if the browser can likely play this format
 */
export function isBrowserPlayable(file: File): boolean {
  const extension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'))
  return !BROWSER_UNSUPPORTED_EXTENSIONS.includes(extension)
}

/**
 * Get the MIME type for a video file.
 * Uses the browser-reported MIME type if valid, otherwise falls back to extension mapping.
 * This handles formats like MKV where browsers don't report the correct MIME type.
 *
 * @param file - The video file
 * @returns The MIME type string
 */
export function getVideoMimeType(file: File): string {
  // If browser reports a valid video MIME type, use it
  if (file.type && file.type.startsWith('video/')) {
    return file.type
  }
  // Fall back to extension mapping
  const extension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'))
  return EXTENSION_TO_MIME[extension] || 'video/mp4' // Default to mp4 if unknown
}

/**
 * Maximum file size for uploads (100 MB).
 */
export const MAX_UPLOAD_FILE_SIZE_BYTES = 100 * 1024 * 1024

/**
 * Error codes for video validation failures.
 */
export type VideoValidationErrorCode = 'INVALID_FORMAT' | 'INVALID_MIME' | 'FILE_TOO_LARGE'

/**
 * Validation result for a video file.
 */
export interface VideoValidationResult {
  valid: boolean
  error?: string
  errorCode?: VideoValidationErrorCode
  fileSizeBytes?: number
}

/**
 * Extracted video metadata.
 */
export interface ExtractedVideoMetadata {
  duration: number // in seconds
  width: number
  height: number
}

/**
 * Validates a video file for upload.
 * Checks file size, extension, and MIME type.
 *
 * @param file - The file to validate
 * @returns Validation result with error message if invalid
 */
export function validateVideoFile(file: File): VideoValidationResult {
  // Check file size first
  if (file.size > MAX_UPLOAD_FILE_SIZE_BYTES) {
    return {
      valid: false,
      error: 'File size exceeds 100 MB limit',
      errorCode: 'FILE_TOO_LARGE',
      fileSizeBytes: file.size,
    }
  }

  // Check file extension
  const extension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'))
  if (!ALLOWED_EXTENSIONS.includes(extension)) {
    return {
      valid: false,
      error: `Invalid file format. Allowed: ${ALLOWED_EXTENSIONS.join(', ')}`,
      errorCode: 'INVALID_FORMAT',
    }
  }

  // Check MIME type (be lenient for formats where browsers report inconsistent MIME types)
  // If extension is valid (checked above), accept any video/* MIME type or empty/generic types
  const hasValidMimeType = ALLOWED_MIME_TYPES.includes(file.type)
  const hasEmptyOrGenericMimeType = file.type === '' || file.type === 'application/octet-stream'
  const hasAnyVideoMimeType = file.type.startsWith('video/')
  if (!hasValidMimeType && !hasEmptyOrGenericMimeType && !hasAnyVideoMimeType) {
    return {
      valid: false,
      error: `Invalid video type. Allowed: ${SUPPORTED_VIDEO_LABELS.join(', ')}`,
      errorCode: 'INVALID_MIME',
    }
  }

  return { valid: true }
}

/**
 * Extracts metadata from a video file using HTML5 video element.
 *
 * @param file - The video file to extract metadata from
 * @returns Promise resolving to extracted metadata
 * @throws Error if video cannot be loaded or is corrupt
 */
export function extractVideoMetadata(file: File): Promise<ExtractedVideoMetadata> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video')
    const objectUrl = URL.createObjectURL(file)

    const cleanup = () => {
      URL.revokeObjectURL(objectUrl)
      video.remove()
    }

    video.addEventListener('loadedmetadata', () => {
      const metadata: ExtractedVideoMetadata = {
        duration: video.duration,
        width: video.videoWidth,
        height: video.videoHeight,
      }
      cleanup()
      resolve(metadata)
    })

    video.addEventListener('error', () => {
      cleanup()
      reject(new Error('Failed to load video. The file may be corrupt or in an unsupported format.'))
    })

    // Set a timeout for loading
    const timeout = setTimeout(() => {
      cleanup()
      reject(new Error('Video loading timed out. The file may be too large or corrupt.'))
    }, 30000) // 30 second timeout

    video.addEventListener('loadedmetadata', () => clearTimeout(timeout))
    video.addEventListener('error', () => clearTimeout(timeout))

    video.preload = 'metadata'
    video.src = objectUrl
  })
}

/**
 * Generates a thumbnail from a video file.
 * Seeks to approximately 1 second (or 10% of duration for short videos)
 * and captures a frame.
 *
 * @param file - The video file to generate thumbnail from
 * @param targetTime - Optional specific time to capture (in seconds)
 * @returns Promise resolving to thumbnail as blob URL
 * @throws Error if thumbnail cannot be generated
 */
export function generateVideoThumbnail(file: File, targetTime?: number): Promise<string> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video')
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    const objectUrl = URL.createObjectURL(file)

    if (!ctx) {
      reject(new Error('Failed to create canvas context'))
      return
    }

    const cleanup = () => {
      URL.revokeObjectURL(objectUrl)
      video.remove()
      canvas.remove()
    }

    video.addEventListener('loadedmetadata', () => {
      // Determine seek time: use targetTime, or 1 second, or 10% of duration
      const seekTime = targetTime ?? Math.min(1, video.duration * 0.1)
      video.currentTime = seekTime
    })

    video.addEventListener('seeked', () => {
      // Set canvas size to video dimensions (scaled down for thumbnail)
      const maxSize = 320
      const scale = Math.min(maxSize / video.videoWidth, maxSize / video.videoHeight, 1)
      canvas.width = video.videoWidth * scale
      canvas.height = video.videoHeight * scale

      // Draw the frame
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height)

      // Convert to blob URL
      canvas.toBlob(
        blob => {
          cleanup()
          if (blob) {
            resolve(URL.createObjectURL(blob))
          } else {
            reject(new Error('Failed to generate thumbnail blob'))
          }
        },
        'image/jpeg',
        0.8,
      )
    })

    video.addEventListener('error', () => {
      cleanup()
      reject(new Error('Failed to load video for thumbnail generation'))
    })

    // Set a timeout for thumbnail generation
    const timeout = setTimeout(() => {
      cleanup()
      reject(new Error('Thumbnail generation timed out'))
    }, 30000) // 30 second timeout

    video.addEventListener('seeked', () => clearTimeout(timeout))
    video.addEventListener('error', () => clearTimeout(timeout))

    video.preload = 'metadata'
    video.src = objectUrl
  })
}

/**
 * Checks if a file is a video based on MIME type or extension.
 * Note: Some browsers don't report correct MIME types for certain formats (e.g., MKV).
 *
 * @param file - The file to check
 * @returns True if the file is a video
 */
export function isVideoFile(file: File): boolean {
  // Check MIME type first
  if (file.type.startsWith('video/')) {
    return true
  }
  // Fall back to extension check for formats with unreliable MIME types (e.g., MKV)
  const extension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'))
  return ALLOWED_EXTENSIONS.includes(extension)
}

/**
 * Checks if a DataTransfer contains video files.
 * Note: During dragover, we can only check MIME types. For formats with unreliable
 * MIME types (e.g., MKV), we allow any file through and validate on drop.
 *
 * @param dataTransfer - The DataTransfer from drag event
 * @returns True if at least one video file is present
 */
export function hasVideoFiles(dataTransfer: DataTransfer): boolean {
  if (dataTransfer.types.includes('Files')) {
    for (let i = 0; i < dataTransfer.items.length; i++) {
      const item = dataTransfer.items[i]
      if (item.kind === 'file') {
        // Check MIME type
        if (item.type.startsWith('video/')) {
          return true
        }
        // For empty or generic MIME types, allow through (will validate on drop)
        // This handles MKV and other formats that browsers don't identify correctly
        if (item.type === '' || item.type === 'application/octet-stream') {
          return true
        }
      }
    }
  }
  return false
}

/**
 * Gets the first video file from a DataTransfer.
 *
 * @param dataTransfer - The DataTransfer from drag event
 * @returns The first video file, or null if none found
 */
export function getVideoFileFromDataTransfer(dataTransfer: DataTransfer): File | null {
  if (dataTransfer.files.length > 0) {
    for (let i = 0; i < dataTransfer.files.length; i++) {
      const file = dataTransfer.files[i]
      if (isVideoFile(file)) {
        return file
      }
    }
  }
  return null
}
